import { Directive, EventEmitter, HostListener, NgZone, OnDestroy, Output } from '@angular/core';

import { forkJoin, Observable, of, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';

@Directive({
    standalone: false,
    selector: '[digihavenDragDrop]'
})
export class DragAndDropDirective implements OnDestroy {
    private readonly MAX_DEPTH: number = 5;
    private readonly destroy$: Subject<void> = new Subject<void>();

    @Output()
    public readonly filesDropped: EventEmitter<File[]> = new EventEmitter<File[]>();

    constructor(private ngZone: NgZone) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @HostListener('dragover', ['$event'])
    public onFileDrag(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
    }

    @HostListener('drop', ['$event'])
    public onFileDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        const files: DataTransferItemList | undefined = event.dataTransfer?.items;

        if (!files) {
            // Potentially non-supported browser
            if (event.dataTransfer?.files) {
                const fileList: FileList = event.dataTransfer.files;
                this.ngZone.run(() => this.filesDropped.emit(Array.from(fileList)));
            }
            return;
        }

        const fileItems: Observable<File[]>[] = this.readFileTree(files);

        forkJoin(fileItems)
            .pipe(
                map(filesToFlatten => filesToFlatten.flat()),
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe(flatFiles => this.ngZone.run(() => this.filesDropped.next(flatFiles)));
    }

    private readFileTree(files: DataTransferItemList): Observable<File[]>[] {
        const fileItems: Observable<File[]>[] = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i].webkitGetAsEntry();
            if (file) {
                fileItems.push(this.traverseFileTree(file));
            }
        }
        return fileItems;
    }

    private traverseFileTree(file: any, path?: string, depth: number = 0): Observable<File[]> {
        if (!file || depth >= this.MAX_DEPTH) {
            return of([]);
        }

        const realPath: string = path || '';

        if (file.isFile) {
            // Get file
            return new Observable<File[]>(observer =>
                file.file((item: File) => {
                    observer.next([item]);
                    observer.complete();
                })
            );
        } else if (file.isDirectory) {
            // Get folder contents
            const dirReader = file.createReader();

            return new Observable<Observable<File[]>[]>(observer =>
                dirReader.readEntries((entries: any) => {
                    const observables: Observable<File[]>[] = [];
                    const newDepth: number = depth + 1;
                    for (const entry of entries) {
                        observables.push(this.traverseFileTree(entry, realPath + file.name + '/', newDepth));
                    }

                    observer.next(observables);
                    observer.complete();
                })
            ).pipe(
                switchMap((nested: Observable<File[]>[]): Observable<File[][]> => forkJoin(nested)),
                map(nestedFiles => nestedFiles.flat())
            );
        } else {
            return of([]);
        }
    }
}
